import type { Nullable } from '@amzn/elevate-data-types';
import { AwsRum, type AwsRumConfig } from 'aws-rum-web';
import { type Context, createContext, useContext } from 'react';

import amplifyConfig from '@/amplifyconfiguration.json';
import featureFlags from '@/utilities/feature-flags';

export type IAwsRumContext = Nullable<AwsRum>;

type EnvironmentNames = 'devnew' | 'staging' | 'prod';
type IAwsRumProvider = { children: React.ReactNode };

const environment: EnvironmentNames = import.meta.env.REACT_APP_BUILD_ENV;
const isDevelopment = import.meta.env.MODE === 'development';
const region = amplifyConfig.aws_project_region;
const version = import.meta.env.PACKAGE_VERSION;

const authConfigs: Record<EnvironmentNames, { guestRoleArn: string; identityPoolId: string }> = {
  devnew: {
    guestRoleArn: 'arn:aws:iam::278804535517:role/RUM-Monitor-us-west-2-278804535517-9061626003071-Unauth',
    identityPoolId: 'us-west-2:bf968c6e-7b6e-45cc-9cbe-9003fe10c45e',
  },
  prod: {
    guestRoleArn: 'arn:aws:iam::497586388326:role/RUM-Monitor-us-west-2-497586388326-5953780203071-Unauth',
    identityPoolId: 'us-west-2:aca2d642-41f2-438b-98cb-5802e005283d',
  },
  staging: {
    guestRoleArn: 'arn:aws:iam::008681337058:role/RUM-Monitor-us-west-2-008681337058-5318270203071-Unauth',
    identityPoolId: 'us-west-2:53db318a-6df6-45ba-9441-bf31eedf2be5',
  },
};

const applicationIDs: Record<EnvironmentNames, string> = {
  devnew: '32ea1168-4b22-458f-9b63-55fa182a0bce',
  prod: 'f9a2dd5f-7a5d-46de-8058-12318cce2084',
  staging: 'd31f4e28-a140-4252-b7a9-950a53bf7809',
};

const applicationID = isDevelopment ? '' : applicationIDs[environment];

const monitorConfig: AwsRumConfig = {
  ...(isDevelopment ? {} : authConfigs[environment]),
  sessionSampleRate: 1,
  endpoint: `https://dataplane.rum.${region}.amazonaws.com`,
  telemetries: ['errors', 'performance', 'http'],
  allowCookies: true,
  enableXRay: featureFlags.XRAY_ENABLED.value,
  enableRumClient: featureFlags.RUM_ENABLED.value,
  disableAutoPageView: true,
};

let client: IAwsRumContext = null;
try {
  client = new AwsRum(applicationID, version, region, monitorConfig);
} catch {
  // noop
}

const AwsRumContext: Context<IAwsRumContext> = createContext<IAwsRumContext>(client);

export function AwsRumProvider({ children }: IAwsRumProvider): React.ReactElement {
  return <AwsRumContext.Provider value={client}>{children}</AwsRumContext.Provider>;
}

export function useAwsRum(): IAwsRumContext {
  return useContext(AwsRumContext);
}
